/**
 * Guide — the hub a new user reads to get set up and to understand how JobCork
 * applies for them. Three parts: getting started, setting up the Chrome
 * extension (the thing that was missing — Copilot and Finish-in-Chrome need
 * it), and how applications go out (Autopilot / Copilot / Manual, and the
 * Needs-you pause). The definitions are also where a job's tag links to.
 */

import { browserFrom } from "./fillHint";
import type { ExtensionStatus } from "./extension";

const BROWSER = browserFrom(
  navigator.userAgent,
  (navigator as unknown as { userAgentData?: { brands?: ReadonlyArray<{ brand: string }> } }).userAgentData?.brands ?? null,
);

/** Set once the extension is published; until then the install step is instructions only. */
const STORE_URL = "";

function CloudIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--ok)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.5 19a4.5 4.5 0 0 0 0-9 6 6 0 0 0-11.6-1.5A4 4 0 0 0 6.5 19z" /><path d="M9 14.5l2 2 4-4" /></svg>;
}
function BrowserIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--ok)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="15" rx="2" /><path d="M3 8h18" /></svg>;
}
function HandIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11.5V4.5a1.5 1.5 0 0 1 3 0V11" /><path d="M12 11V3.5a1.5 1.5 0 0 1 3 0V11" /><path d="M15 11V5.5a1.5 1.5 0 0 1 3 0V13a6 6 0 0 1-6 6h-1.5a5 5 0 0 1-4-2l-2.5-3.3a1.5 1.5 0 0 1 2.3-1.9L9 14" /></svg>;
}
function QIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--err)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3" /><path d="M12 17h.01" /><circle cx="12" cy="12" r="9" /></svg>;
}

const START: Array<{ go: string; label: string; hint: string }> = [
  { go: "resume", label: "Upload your resume", hint: "We read the text out of it — you correct anything the parser got wrong, then confirm it. Everything is written from this." },
  { go: "preferences", label: "Set your search types", hint: "Titles, places and a pay floor — obeyed exactly, never widened. Have more than one kind of role? Add a track for each (IT Security, Test Manager) in Settings; each keeps its own resume, search and matches." },
  { go: "answers", label: "Confirm your answers", hint: "Work authorization, location, and the rest that forms ask. We never guess these — they go out under your name exactly as you confirm them." },
];

export default function Guide({ onBack, onGo, ext }: { onBack: () => void; onGo: (go: string) => void; ext: ExtensionStatus | null }) {
  const connected = ext?.runner?.connected ?? false;
  const installed = ext?.installed ?? false;
  const extState = BROWSER === "other"
    ? { tone: "muted", text: "This browser isn't Chrome. Copilot and “Finish in Chrome” run in Chrome (or Edge, Brave, Arc) with the extension — you can still use “Send with JobCork” anywhere." }
    : connected
      ? { tone: "ok", text: "This browser: connected. Copilot is on here." }
      : installed
        ? { tone: "err", text: "This browser: installed, but not connected yet — finish step 3." }
        : { tone: "err", text: "This browser: the extension isn't installed yet — start at step 1." };

  return (
    <>
      <header className="bar">
        <div>
          <h1 className="tight">Guide</h1>
          <p className="muted small">Everything to get set up, and how JobCork applies for you.</p>
        </div>
        <button className="btn ghost small" onClick={onBack}>Back</button>
      </header>

      {/* Part 1 — Getting started */}
      <h2>Getting started</h2>
      <ul className="g-steps">
        {START.map((s, i) => (
          <li className="g-step" key={s.go}>
            <span className="g-n">{i + 1}</span>
            <div className="g-b">
              <button className="g-link" onClick={() => onGo(s.go)}>{s.label} →</button>
              <p className="muted small nomargin">{s.hint}</p>
            </div>
          </li>
        ))}
      </ul>

      {/* Part 2 — Set up the extension */}
      <h2>Set up the extension</h2>
      <div className={`g-extstate ${extState.tone}`}><span>{extState.text}</span></div>
      <p className="muted small">
        The JobCork extension lets applications go out from your <strong>own Chrome</strong> — that's <strong>Copilot</strong> and <strong>“Finish in Chrome.”</strong> Without it, JobCork still sends from the cloud (Autopilot) and you can approve one at a time with “Send with JobCork”; but the extension clears the robot checks the cloud can't, so it's worth two minutes.
      </p>
      <ul className="g-steps">
        <li className="g-step">
          <span className="g-n">1</span>
          <div className="g-b">
            <p className="g-steplabel">Install it, then pin it</p>
            <p className="muted small nomargin">Add JobCork to Chrome (Edge, Brave and Arc work too), then click the puzzle-piece icon and the pin next to JobCork so it stays in your toolbar.</p>
            {STORE_URL
              ? <a className="btn small" href={STORE_URL} target="_blank" rel="noreferrer noopener" style={{ marginTop: 8, display: "inline-block" }}>Get the extension</a>
              : <p className="muted small nomargin" style={{ marginTop: 6 }}>The one-click install link appears here once JobCork is on the Chrome Web Store.</p>}
          </div>
        </li>
        <li className="g-step">
          <span className="g-n">2</span>
          <div className="g-b">
            <p className="g-steplabel">Be signed in here</p>
            <p className="muted small nomargin">You're signed in to jobcork.com in this browser — the extension gets your sign-in from the app and never asks for a password.</p>
          </div>
        </li>
        <li className="g-step">
          <span className="g-n">3</span>
          <div className="g-b">
            <p className="g-steplabel">Connect this browser</p>
            <p className="muted small nomargin">The last step: on Auto-apply, turn Auto on and press “Connect this browser.” You'll see “Connected in this browser,” and Copilot jobs start sending while Chrome is open.</p>
            <button className="btn small" style={{ marginTop: 8 }} onClick={() => onGo("auto-apply")}>Go to Auto-apply →</button>
          </div>
        </li>
      </ul>

      {/* Part 3 — How applications go out */}
      <h2>How applications go out</h2>
      <p className="muted small">Three ways to send, and one thing that can pause any of them. Every match is tagged with the one it'll use, so you always know what will happen. JobCork works out the tag per employer from real sends, and never tries to beat a security check.</p>
      <div className="g-routes">
        <div className="g-rt auto">
          <div className="g-rt-top"><span className="g-rt-ic auto"><CloudIcon /></span><h3>Autopilot</h3></div>
          <p className="small"><strong>The cloud sends it, computer off.</strong> At employers whose form lets our cloud through cleanly.</p>
          <p className="muted small nomargin">You do: nothing. It lands in Applications, sent.</p>
        </div>
        <div className="g-rt browser">
          <div className="g-rt-top"><span className="g-rt-ic browser"><BrowserIcon /></span><h3>Copilot</h3></div>
          <p className="small"><strong>Sends from your own Chrome, while it's open.</strong> Your browser clears the robot check the cloud gets here — it's really you.</p>
          <p className="muted small nomargin">You do: leave Chrome open, with the extension.</p>
        </div>
        <div className="g-rt manual">
          <div className="g-rt-top"><span className="g-rt-ic manual"><HandIcon /></span><h3>Manual</h3></div>
          <p className="small"><strong>We fill it; you press the final button.</strong> Platforms that need your own account (Workday, iCIMS), or sites where repeated auto-submits would risk your standing.</p>
          <p className="muted small nomargin">You do: one click on the employer's page.</p>
        </div>
      </div>
      <div className="g-pause">
        <span className="g-rt-ic needs"><QIcon /></span>
        <div>
          <h3 className="nomargin">Needs you — a question</h3>
          <p className="small nomargin">The form asked something your saved answers don't cover, so JobCork stops rather than guess. It can pause an Autopilot, a Copilot or a Manual one — answer it once and it continues, and every later form that asks the same thing gets your answer automatically.</p>
        </div>
      </div>
    </>
  );
}
